import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, BadgeCheck, Loader2, Search, Copy, Check, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';
import BrandLogo from '../components/BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminDirectoryPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedWidget, setCopiedWidget] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    credential_type: 'Influencer',
    platforms: '',
    website: '',
    bio: '',
    status: 'active'
  });

  useEffect(() => {
    checkAdminAccess();
    loadEntries();
  }, []);

  const checkAdminAccess = () => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user && user.is_admin) {
      setIsAdmin(true);
    } else {
      setError('Admin access required');
    }
  };

  const loadEntries = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/directory/admin/all`, { withCredentials: true });
      setEntries(response.data.entries || []);
    } catch (err) {
      console.error('Failed to load directory:', err);
      setError('Failed to load directory entries');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        credential_type: formData.credential_type,
        verification_expiry_months: 12
      };

      if (editingEntry) {
        await axios.put(`${API}/directory/admin/update/${editingEntry.entry_id}`, {
          name: formData.name,
          credential_type: formData.credential_type,
          verification_status: formData.status === 'active' ? 'Active' : formData.status === 'suspended' ? 'Suspended' : 'Expired',
          opted_in: formData.status === 'active'
        }, { withCredentials: true });
        setSuccess('Entry updated successfully');
      } else {
        await axios.post(`${API}/directory/admin/add`, payload, { withCredentials: true });
        setSuccess('Entry added successfully');
      }
      
      setShowAddModal(false);
      setEditingEntry(null);
      resetForm();
      loadEntries();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      name: entry.name || '',
      email: entry.email || '',
      credential_type: entry.credential_type || 'Influencer',
      platforms: Array.isArray(entry.platforms) ? entry.platforms.join(', ') : '',
      website: entry.website || '',
      bio: entry.bio || '',
      status: entry.verification_status === 'Active' ? 'active' : entry.verification_status === 'Suspended' ? 'suspended' : 'expired'
    });
    setShowAddModal(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      await axios.delete(`${API}/directory/admin/remove/${entryId}`, { withCredentials: true });
      setSuccess('Entry deleted successfully');
      loadEntries();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const handleShowWidget = (entry) => {
    setSelectedEntry(entry);
    setShowWidgetModal(true);
  };

  const getWidgetCode = (entry) => {
    const baseUrl = window.location.origin;
    return `<!-- KaiCheckIt Verified Badge -->
<a href="${baseUrl}/verify/${entry.entry_id}" target="_blank" rel="noopener" style="display:inline-block;text-decoration:none;">
  <img src="${baseUrl}/api/widget/badge/${entry.entry_id}" alt="KaiCheckIt Verified" style="height:40px;"/>
</a>`;
  };

  const copyWidgetCode = (entry) => {
    navigator.clipboard.writeText(getWidgetCode(entry));
    setCopiedWidget(true);
    setTimeout(() => setCopiedWidget(false), 2000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      credential_type: 'Influencer',
      platforms: '',
      website: '',
      bio: '',
      status: 'active'
    });
  };

  const filteredEntries = entries.filter(entry =>
    entry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <Card className="p-8 dark:bg-slate-800">
          <p className="text-red-600 dark:text-red-400">Admin access required</p>
          <Button onClick={() => navigate('/')} className="mt-4">Go Home</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo variant="plain" size="lg" data-testid="brand-logo-standalone" />
            <span className="text-xl font-bold text-blue-600 dark:text-blue-400">KaiCheckIt</span>
            <Badge variant="outline" className="ml-2">Admin</Badge>
          </div>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Title */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100">Manage Directory</h1>
              <p className="text-slate-600 dark:text-slate-400">Add, edit, and manage verified influencers</p>
            </div>
            <Button onClick={() => { resetForm(); setEditingEntry(null); setShowAddModal(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </div>

          {/* Alerts */}
          {error && (
            <Alert className="mb-4 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800">
              <AlertDescription className="text-red-800 dark:text-red-300">{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="mb-4 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800">
              <AlertDescription className="text-green-800 dark:text-green-300">{success}</AlertDescription>
            </Alert>
          )}

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 dark:placeholder:text-slate-500"
              />
            </div>
          </div>

          {/* Entries List */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
            </div>
          ) : filteredEntries.length === 0 ? (
            <Card className="p-8 text-center dark:bg-slate-800">
              <BadgeCheck className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400">No directory entries yet</p>
              <Button onClick={() => setShowAddModal(true)} className="mt-4">
                Add First Entry
              </Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredEntries.map((entry) => (
                <Card key={entry.entry_id} className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-md">
                          <BadgeCheck className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800 dark:text-slate-100">{entry.name}</h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{entry.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs dark:border-slate-600 dark:text-slate-300">{entry.credential_type}</Badge>
                            <Badge className={entry.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'}>
                              {entry.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleShowWidget(entry)} className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
                          <Copy className="w-4 h-4 mr-1" />
                          Widget
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(entry)} className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/30" onClick={() => handleDelete(entry.entry_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">{editingEntry ? 'Edit Entry' : 'Add Directory Entry'}</DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {editingEntry ? 'Update the verified influencer details' : 'Add a new verified influencer to the directory'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="dark:text-slate-200">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                required
              />
            </div>
            <div>
              <Label htmlFor="email" className="dark:text-slate-200">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                required
              />
            </div>
            <div>
              <Label htmlFor="credential_type" className="dark:text-slate-200">Credential Type</Label>
              <Select value={formData.credential_type} onValueChange={(v) => setFormData({ ...formData, credential_type: v })}>
                <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                  <SelectItem value="Influencer">Influencer</SelectItem>
                  <SelectItem value="Journalist">Journalist</SelectItem>
                  <SelectItem value="Content Creator">Content Creator</SelectItem>
                  <SelectItem value="Public Figure">Public Figure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="platforms" className="dark:text-slate-200">Platforms (comma-separated)</Label>
              <Input
                id="platforms"
                placeholder="YouTube, TikTok, Instagram"
                value={formData.platforms}
                onChange={(e) => setFormData({ ...formData, platforms: e.target.value })}
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100 dark:placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label htmlFor="website" className="dark:text-slate-200">Website</Label>
              <Input
                id="website"
                type="url"
                placeholder="https://..."
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100 dark:placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label htmlFor="bio" className="dark:text-slate-200">Bio</Label>
              <Textarea
                id="bio"
                placeholder="Short bio..."
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100 dark:placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label htmlFor="status" className="dark:text-slate-200">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingEntry ? 'Update' : 'Add Entry'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Widget Modal */}
      <Dialog open={showWidgetModal} onOpenChange={setShowWidgetModal}>
        <DialogContent className="max-w-lg dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">Embed Widget for {selectedEntry?.name}</DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Copy this code to embed the verified badge on LinkTree, websites, or digital cards
            </DialogDescription>
          </DialogHeader>
          {selectedEntry && (
            <div className="space-y-4">
              {/* Preview */}
              <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-6 text-center border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">Preview:</p>
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-2 rounded-full shadow-md">
                  <BadgeCheck className="w-5 h-5" />
                  <span className="font-semibold">KaiCheckIt Verified</span>
                </div>
              </div>
              
              {/* Code */}
              <div>
                <Label className="text-slate-700 dark:text-slate-300">Embed Code:</Label>
                <div className="relative mt-1">
                  <pre className="bg-slate-900 text-slate-100 dark:bg-slate-950 p-4 rounded-lg text-xs overflow-x-auto border border-slate-700">
                    {getWidgetCode(selectedEntry)}
                  </pre>
                  <Button
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => copyWidgetCode(selectedEntry)}
                  >
                    {copiedWidget ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Verification URL */}
              <div>
                <Label className="text-slate-700 dark:text-slate-300">Verification URL:</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input 
                    readOnly 
                    value={`${window.location.origin}/verify/${selectedEntry.entry_id}`}
                    className="text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200"
                  />
                  <Button variant="outline" size="sm" onClick={() => window.open(`/verify/${selectedEntry.entry_id}`, '_blank')}>
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDirectoryPage;
